import { configGAM, configGTM, configSite } from "@pub/config";
import { Gam, type GAMProps } from "@sphtech/dmg-design-system";
import type {
  PageTargetingContext,
  PageTargetingValue,
} from "@sphtech/web2-ads";

import type { StoryData } from "../../types/ServerCustomContext";
import type { PageAdsContext } from "./pageAds";

type Props = Omit<GAMProps, "StoryData"> & {
  StoryData?: Partial<StoryData>;
};

function toGamStoryData(storyData?: Partial<StoryData>): GAMProps["StoryData"] {
  return {
    id: storyData?.id || "",
    published: storyData?.publishDate || "",
    updated: storyData?.updateDate || "",
    authors: storyData?.authors || [],
    tags: storyData?.tags || [],
    typeName: storyData?.typeName || "",
    urlPath: storyData?.path || "",
    sections: storyData?.sections || [],
    sponsorship: storyData?.sponsorship || {},
  };
}

export function buildLegacyGamBaseData({
  sectionName,
  pageType,
  pageTitle,
  adUnits,
  StoryData,
  currentLocation,
}: Props) {
  const { baseData } = Gam({
    sectionName,
    pageType,
    pageTitle,
    adUnits,
    categoryPrefix: configGAM.categoryPrefix || "",
    StoryData: toGamStoryData(StoryData),
    currentLocation,
    siteConfig: {
      siteName: configSite.name,
      siteAddress: configSite.name,
      gtmContainer: configGTM.container,
      adAccount: configGAM.account,
      adAbm: !!configGAM.enableAbm,
    },
  });

  return baseData;
}

export function publishLegacyGamBaseData(baseData: Record<string, unknown>) {
  if (typeof window === "undefined") return;
  window.sphm_base_data = baseData;
}

export function publishLegacyRecommendationSignals() {
  if (typeof window === "undefined") return;

  window.outbrain_enable ??= 1;
  window.taboola_enable ??= 1;
  window.postMessage("outbrain_enable", window.location.origin);
  window.postMessage("taboola_enable", window.location.origin);
}

export function getLegacyTargetPage(context: PageAdsContext) {
  if (context.adSection === "tag") return "tag";
  if (context.contentKind === "index") return "home";
  return context.contentKind;
}

function getLegacySkinVersion() {
  if (typeof window === "undefined") return undefined;

  // Legacy gam.js bonzai_skin_custom_key() measures the viewport
  // (screen_width() = window.innerWidth), not the monitor.
  const skinSafeArea = 340;
  const skinSpace = window.innerWidth - 1060;
  return skinSpace >= skinSafeArea ? "1" : "2";
}

// Legacy gam.js rolled impression_split once per page load (set_targeting ran
// once); memoise it so targeting-context rebuilds (re-renders, SPA
// navigations) can't reshuffle the bucket mid-session.
let impressionSplit: string | undefined;

function buildLegacyCustomTargeting(): Record<string, PageTargetingValue> {
  if (typeof window === "undefined") return {};

  const searchParams = new URLSearchParams(window.location.search);
  const custom: Record<string, PageTargetingValue> = {
    impression_split: (impressionSplit ??= String(
      Math.floor(Math.random() * 20) + 1,
    )),
  };
  const skinver = getLegacySkinVersion();
  if (skinver) {
    custom.skinver = skinver;
  }

  const dfpPreview = searchParams.get("dfp_preview");
  if (dfpPreview) {
    custom.dfp_preview = dfpPreview;
  }

  const testAdId = searchParams.get("test_adid");
  if (testAdId) {
    custom.test_adid = testAdId;
  }

  const testPlId = searchParams.get("test_plid");
  if (testPlId) {
    custom.test_plid = testPlId;
  }

  return custom;
}

export function buildLegacyPageTargetingContext(
  context: PageAdsContext,
): PageTargetingContext {
  return {
    pageType: getLegacyTargetPage(context),
    articleId: context.articleId ?? null,
    articleTags: context.articleTags ?? null,
    pageNumber: 1,
    custom: buildLegacyCustomTargeting(),
  };
}
